import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import Container from "@mui/material/Container";
import { Form, Input, InputNumber, Select } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import dayjs from "dayjs";

import {
  MentorshipProgramModule_Insert_Input,
  MentorshipProgram_Insert_Input,
} from "../../graphql/generated/graphql";
import {
  useGetWorkspaceLocationsByWorkspaceIdQuery,
  GetWorkspaceLocationsByWorkspaceIdQuery,
} from "../../graphql/queries/getWorkspaceLocationsByWorkspaceId.generated";
import { useGetMentorshipProgramByIdQuery } from "../../graphql/queries/getMentorshipProgramById.generated";
import { useUpdateMentorshipProgramModulesMutation } from "../../graphql/mutations/updateMentorshipProgramModules.generated";
import { useUpdateMentorshipProgramByIdMutation } from "../../graphql/mutations/updateMentorshipProgramById.generated";
import { useUpdateMentorshipProgramSurveyByIdMutation } from "../../graphql/mutations/updateMentorshipProgramSurveyById.generated";
import { useAddMentorshipProgramSurveyMutation } from "../../graphql/mutations/addMentorshipProgramSurvey.generated";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import { SurveyAvailability } from "../../constants/input-values";

import {
  SortableModuleItem,
  surveyOptions,
} from "../mentorship-program-add/mentorship-program-add";

type Option = { value: string | number | null; label: string };

export default function MentorshipProgramEdit() {
  const { loggedUserData } = useAuth();
  const params = useParams();
  const programId = params.programId ? params.programId : "";

  useEffect(() => {
    console.log("MentorshipProgramDetails programId: ", programId);
  }, [params]);

  const navigate = useNavigate();

  const [workspaceId, setWorkspaceId] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [mentorLabel, setMentorLabel] = useState<string>("");
  const [menteeLabel, setMenteeLabel] = useState<string>("");
  const [matchingMethod, setMatchingMethod] = useState<string | null>(null);
  const [modulesQuantity, setModulesQuantity] = useState<number>(2);
  const [availability, setAvailability] = useState<number | null>(null);

  const [preSurveyLink, setPreSurveyLink] = useState<string>("");
  const [preSurveyAvailability, setPreSurveyAvailability] = useState<
    string | null
  >(null);
  const [midSurveyLink, setMidSurveyLink] = useState<string>("");
  const [midSurveyAvailability, setMidSurveyAvailability] = useState<
    string | null
  >(null);
  const [postSurveyLink, setPostSurveyLink] = useState<string>("");
  const [postSurveyAvailability, setPostSurveyAvailability] = useState<
    string | null
  >(null);
  const [modules, setModules] = useState<
    Array<MentorshipProgramModule_Insert_Input>
  >([]);

  const [completedOperations, setCompletedOperations] = useState<{
    program: boolean;
    modules: boolean;
    survey: boolean;
  }>({
    program: false,
    modules: false,
    survey: false,
  });

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }
  }, [loggedUserData]);

  // Function to check if all operations are complete
  const checkAllOperationsComplete = useCallback(
    (operations: typeof completedOperations) => {
      if (operations.program && operations.modules && operations.survey) {
        console.log("All operations completed, navigating...");

        if (programId) {
          const path = `${routeNames.MENTORSHIP_DETAILS.replace(
            ":programId",
            `${programId}`
          )}`;

          navigate(path);
        } else {
          navigate(routeNames.MENTORSHIP);
        }
      }
    },
    [navigate, programId]
  );

  const updateModule = useCallback(
    (index: number, patch: Partial<MentorshipProgramModule_Insert_Input>) => {
      setModules((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], ...patch };
        return copy;
      });
    },
    []
  );

  const handleOnCompletedGetWorkspaceLocationsByWorkspaceId = useCallback(
    (data: GetWorkspaceLocationsByWorkspaceIdQuery) => {
      // console.log("Get Workspace Locations data: ", data);
    },
    []
  );

  const handleOnErrorsGetWorkspaceLocationsByWorkspaceId = useCallback(
    (error: ApolloError) => {
      console.log("Get Workspace Locations error: ", error);
    },
    []
  );

  const { data: workspaceLocationsData, loading: workspaceLocationsLoading } =
    useGetWorkspaceLocationsByWorkspaceIdQuery({
      skip: !workspaceId,
      variables: {
        workspaceId: workspaceId as any,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetWorkspaceLocationsByWorkspaceId,
      onError: handleOnErrorsGetWorkspaceLocationsByWorkspaceId,
    });

  const handleOnCompletedGetMentorshipProgramById = useCallback((data: any) => {
    console.log("Get MentorshipProgram ById data: ", data);
    const { mentorshipProgram_by_pk } = data;
    const {
      name,
      mentorLabel,
      menteeLabel,
      matchingMethod,
      mentorshipProgramModules,
      workspaceLocationId,
      mentorshipSurveys,
    } = mentorshipProgram_by_pk || {};

    if (name) {
      setName(name);
    }

    if (workspaceLocationId) {
      setAvailability(workspaceLocationId);
    }

    if (mentorLabel) {
      setMentorLabel(mentorLabel)
    }

    if (menteeLabel) {
      setMenteeLabel(menteeLabel)
    }

    if (matchingMethod) {
      setMatchingMethod(matchingMethod)
    }

    setModulesQuantity(mentorshipProgramModules.length);

    if (mentorshipProgramModules && mentorshipProgramModules.length) {
      setModules(mentorshipProgramModules);
    }

    if (mentorshipSurveys && mentorshipSurveys.length) {
      const {
        id,
        preSurveyLink,
        preSurveyMentorRequired,
        preSurveyMenteeRequired,
        midSurveyLink,
        midSurveyMentorRequired,
        midSurveyMenteeRequired,
        postSurveyLink,
        postSurveyMentorRequired,
        postSurveyMenteeRequired,
      } = mentorshipSurveys[0];

      if (preSurveyLink) {
        setPreSurveyLink(preSurveyLink);
      }

      if (preSurveyMentorRequired && preSurveyMenteeRequired) {
        setPreSurveyAvailability(SurveyAvailability.BOTH);
      } else if (preSurveyMentorRequired) {
        setPreSurveyAvailability(SurveyAvailability.MENTORS);
      } else if (preSurveyMenteeRequired) {
        setPreSurveyAvailability(SurveyAvailability.MENTEES);
      } else {
        setPreSurveyAvailability(SurveyAvailability.NONE);
      }

      if (midSurveyLink) {
        setMidSurveyLink(midSurveyLink);
      }

      if (midSurveyMentorRequired && midSurveyMenteeRequired) {
        setMidSurveyAvailability(SurveyAvailability.BOTH);
      } else if (midSurveyMentorRequired) {
        setMidSurveyAvailability(SurveyAvailability.MENTORS);
      } else if (midSurveyMenteeRequired) {
        setMidSurveyAvailability(SurveyAvailability.MENTEES);
      } else {
        setMidSurveyAvailability(SurveyAvailability.NONE);
      }

      if (postSurveyLink) {
        setPostSurveyLink(postSurveyLink);
      }

      if (postSurveyMentorRequired && postSurveyMenteeRequired) {
        setPostSurveyAvailability(SurveyAvailability.BOTH);
      } else if (postSurveyMentorRequired) {
        setPostSurveyAvailability(SurveyAvailability.MENTORS);
      } else if (postSurveyMenteeRequired) {
        setPostSurveyAvailability(SurveyAvailability.MENTEES);
      } else {
        setPostSurveyAvailability(SurveyAvailability.NONE);
      }
    }
  }, []);

  const handleOnErrorsGetMentorshipProgramById = useCallback(
    (error: ApolloError) => {
      console.log("Get MentorshipProgram ById error: ", error);
    },
    []
  );

  const { data: mentorshipProgramData, loading: mentorshipProgramLoading } =
    useGetMentorshipProgramByIdQuery({
      skip: !programId,
      variables: {
        id: Number(programId),
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetMentorshipProgramById,
      onError: handleOnErrorsGetMentorshipProgramById,
    });

  const handleOnCompletedUpdateMentorshipProgramModules = useCallback(
    (data: any) => {
      console.log("Update MentorshipProgram Modules Completed: ", data);

      setCompletedOperations((prev) => {
        const updated = { ...prev, modules: true };
        checkAllOperationsComplete(updated);
        return updated;
      });
    },
    [checkAllOperationsComplete]
  );

  const handleOnErrorsUpdateMentorshipProgramModules = useCallback(
    (error: ApolloError) => {
      console.log("Update MentorshipProgram Modules error: ", error);
    },
    []
  );

  const [
    updateMentorshipProgramModulesMutation,
    {
      loading: loadingUpdateMentorshipProgramModules,
      error: errorUpdateMentorshipProgramModules,
    },
  ] = useUpdateMentorshipProgramModulesMutation({
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedUpdateMentorshipProgramModules,
    onError: handleOnErrorsUpdateMentorshipProgramModules,
  });

  const handleOnCompletedUpdateMentorshipProgramById = useCallback(
    (data: any) => {
      console.log("Update MentorshipProgram ById Completed: ", data);

      setCompletedOperations((prev) => {
        const updated = { ...prev, program: true };
        checkAllOperationsComplete(updated);
        return updated;
      });
    },
    [checkAllOperationsComplete]
  );

  const handleOnErrorsUpdateMentorshipProgramById = useCallback(
    (error: ApolloError) => {
      console.log("Update MentorshipProgram ById error: ", error);
    },
    []
  );

  const [
    updateMentorshipProgramByIdMutation,
    {
      loading: loadingUpdateMentorshipProgramById,
      error: errorUpdateMentorshipProgramById,
    },
  ] = useUpdateMentorshipProgramByIdMutation({
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedUpdateMentorshipProgramById,
    onError: handleOnErrorsUpdateMentorshipProgramById,
  });

  const handleOnCompletedUpdateMentorshipProgramSurveyById = useCallback(
    (data: any) => {
      console.log("Update MentorshipProgram Survey ById Completed: ", data);

      setCompletedOperations((prev) => {
        const updated = { ...prev, survey: true };
        checkAllOperationsComplete(updated);
        return updated;
      });
    },
    [checkAllOperationsComplete]
  );

  const handleOnErrorsUpdateMentorshipProgramSurveyById = useCallback(
    (error: ApolloError) => {
      console.log("Update MentorshipProgram Survey ById error: ", error);
    },
    []
  );

  const [
    updateMentorshipProgramSurveyByIdMutation,
    {
      loading: loadingUpdateMentorshipProgramSurveyById,
      error: errorUpdateMentorshipProgramSurveyById,
    },
  ] = useUpdateMentorshipProgramSurveyByIdMutation({
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedUpdateMentorshipProgramSurveyById,
    onError: handleOnErrorsUpdateMentorshipProgramSurveyById,
  });

  const handleOnCompletedAddMentorshipProgramSurvey = useCallback(
    (data: any) => {
      console.log("Add MentorshipProgram Survey Completed: ", data);

      setCompletedOperations((prev) => {
        const updated = { ...prev, survey: true };
        checkAllOperationsComplete(updated);
        return updated;
      });
    },
    [checkAllOperationsComplete]
  );

  const handleOnErrorsAddMentorshipProgramSurvey = useCallback(
    (error: ApolloError) => {
      console.log("Add MentorshipProgram Survey error: ", error);
    },
    []
  );

  const [
    addMentorshipProgramSurveyMutation,
    {
      loading: loadingAddMentorshipProgramSurvey,
      error: errorAddMentorshipProgramSurvey,
    },
  ] = useAddMentorshipProgramSurveyMutation({
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedAddMentorshipProgramSurvey,
    onError: handleOnErrorsAddMentorshipProgramSurvey,
  });

  const hasSurveyData = useCallback(() => {
    const hasPreSurvey = preSurveyLink && preSurveyLink.trim() !== "";
    const hasMidSurvey = midSurveyLink && midSurveyLink.trim() !== "";
    const hasPostSurvey = postSurveyLink && postSurveyLink.trim() !== "";

    return hasPreSurvey || hasMidSurvey || hasPostSurvey;
  }, [preSurveyLink, midSurveyLink, postSurveyLink]);

  const handleOnSubmit = useCallback(async () => {
    if (!loggedUserData || !name || !workspaceId || !programId) {
      console.error("Missing required data for submission");
      return;
    }

    // Reset the status of completed operations
    setCompletedOperations({
      program: false,
      modules: false,
      survey: false,
    });

    try {
      // 1. Update the main program
      updateMentorshipProgramByIdMutation({
        variables: {
          programId: Number(programId),
          objects: {
            name: name,
            mentorLabel: mentorLabel,
            menteeLabel: menteeLabel,
            matchingMethod: matchingMethod,
            workspaceLocationId: availability || null,
            updatedAt: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
          },
        },
      });

      // 2. Update modules if they exist
      if (modules && modules.length > 0) {
        const moduleUpdates = modules.map((module, index) => ({
          _set: {
            name: module.name || "",
            content: module.content || "",
            duration: module.duration || null,
            order: index + 1,
            updatedAt: dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
          },
          where: {
            id: { _eq: module.id },
          },
        }));

        // Update modules one by one
        moduleUpdates.forEach((update) => {
          updateMentorshipProgramModulesMutation({
            variables: {
              updates: update,
            },
          });
        });
      } else {
        // If there are no modules to update, mark as completed
        setCompletedOperations((prev) => ({ ...prev, modules: true }));
      }

      // 3. Handle survey - either update existing or create new
      const surveyData =
        mentorshipProgramData?.mentorshipProgram_by_pk?.mentorshipSurveys?.[0];

      console.log("handleOnSubmit surveyData: ", surveyData);

      // Prepare survey boolean values
      const preSurveyMentorRequired =
        preSurveyAvailability === SurveyAvailability.BOTH ||
        preSurveyAvailability === SurveyAvailability.MENTORS;

      const preSurveyMenteeRequired =
        preSurveyAvailability === SurveyAvailability.BOTH ||
        preSurveyAvailability === SurveyAvailability.MENTEES;

      const midSurveyMentorRequired =
        midSurveyAvailability === SurveyAvailability.BOTH ||
        midSurveyAvailability === SurveyAvailability.MENTORS;

      const midSurveyMenteeRequired =
        midSurveyAvailability === SurveyAvailability.BOTH ||
        midSurveyAvailability === SurveyAvailability.MENTEES;

      const postSurveyMentorRequired =
        postSurveyAvailability === SurveyAvailability.BOTH ||
        postSurveyAvailability === SurveyAvailability.MENTORS;

      const postSurveyMenteeRequired =
        postSurveyAvailability === SurveyAvailability.BOTH ||
        postSurveyAvailability === SurveyAvailability.MENTEES;

      if (surveyData?.id) {
        // Update existing survey
        updateMentorshipProgramSurveyByIdMutation({
          variables: {
            id: surveyData.id,
            objects: {
              preSurveyEnabled: !!preSurveyLink,
              preSurveyLink: preSurveyLink || null,
              preSurveyMentorRequired,
              preSurveyMenteeRequired,
              midSurveyEnabled: !!midSurveyLink,
              midSurveyLink: midSurveyLink || null,
              midSurveyMentorRequired,
              midSurveyMenteeRequired,
              postSurveyEnabled: !!postSurveyLink,
              postSurveyLink: postSurveyLink || null,
              postSurveyMentorRequired,
              postSurveyMenteeRequired,
            },
          },
        });
      } else if (hasSurveyData()) {
        // Create new survey if user has added survey data
        addMentorshipProgramSurveyMutation({
          variables: {
            object: {
              createdByUserId: loggedUserData.id,
              mentorshipProgramId: Number(programId),
              preSurveyEnabled: !!preSurveyLink,
              preSurveyLink: preSurveyLink || null,
              preSurveyMentorRequired,
              preSurveyMenteeRequired,
              midSurveyEnabled: !!midSurveyLink,
              midSurveyLink: midSurveyLink || null,
              midSurveyMentorRequired,
              midSurveyMenteeRequired,
              postSurveyEnabled: !!postSurveyLink,
              postSurveyLink: postSurveyLink || null,
              postSurveyMentorRequired,
              postSurveyMenteeRequired,
            },
          },
        });
      } else {
        // If there is no survey data and no existing survey, mark as completed
        setCompletedOperations((prev) => ({ ...prev, survey: true }));
      }
    } catch (error) {
      console.error("Error updating mentorship program:", error);
    }
  }, [
    loggedUserData,
    name,
    mentorLabel,
    menteeLabel,
    matchingMethod,
    workspaceId,
    programId,
    availability,
    modules,
    mentorshipProgramData,
    preSurveyLink,
    preSurveyAvailability,
    midSurveyLink,
    midSurveyAvailability,
    postSurveyLink,
    postSurveyAvailability,
    hasSurveyData,
    updateMentorshipProgramByIdMutation,
    updateMentorshipProgramModulesMutation,
    updateMentorshipProgramSurveyByIdMutation,
    addMentorshipProgramSurveyMutation,
  ]);

  const availabilityOptions = useMemo<Option[]>(() => {
    const defaultOptions: Option[] = [
      { value: null, label: "All mentors and mentees in my organization" },
    ];

    if (workspaceLocationsData?.workspaceLocations.length) {
      const locations: Option[] =
        workspaceLocationsData?.workspaceLocations.map((element) => {
          return { value: element.id, label: element.name };
        });

      return [...defaultOptions, ...locations];
    }

    return defaultOptions;
  }, [workspaceLocationsData]);

  const matchingMethodOptions = useMemo<Option[]>(() => {
    const defaultOptions: Option[] = [
      { value: null, label: "Matching Method" },
      { value: "userRequested", label: "User Requested" },
      { value: "adminManaged", label: "Admin Managed" },
    ];

    return defaultOptions;
  }, [workspaceLocationsData]);

  const preSurveyLinkRequired = useMemo(
    () =>
      preSurveyAvailability != null &&
      preSurveyAvailability !== SurveyAvailability.NONE,
    [preSurveyAvailability]
  );

  const midSurveyLinkRequired = useMemo(
    () =>
      midSurveyAvailability != null &&
      midSurveyAvailability !== SurveyAvailability.NONE,
    [midSurveyAvailability]
  );

  const postSurveyLinkRequired = useMemo(
    () =>
      postSurveyAvailability != null &&
      postSurveyAvailability !== SurveyAvailability.NONE,
    [postSurveyAvailability]
  );

  const isLoading =
    loadingUpdateMentorshipProgramById ||
    loadingUpdateMentorshipProgramModules ||
    loadingUpdateMentorshipProgramSurveyById ||
    loadingAddMentorshipProgramSurvey;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // We use indexes as ids (not persisted; only UI)
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);

    setModules((prev) => {
      const reordered = arrayMove(prev, oldIndex, newIndex);
      // reindex order 1..N
      return reordered.map((m, idx) => ({ ...m, order: idx + 1 }));
    });
  };

  return (
    <Container component="main" maxWidth={false}>
      <div className="mentorship-row">
        <h1>Edit Program</h1>
      </div>
      <div className="mentorship-form-section">
        <span className="mentorship-form-section-title">Program Details</span>
        <span className={"input-label"}>Program Name</span>
        <span className={"input-label-sub"}>What would you like to name this program? For example, RN Residency Mentorship</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            { required: true, message: "Please input the Program name." },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="name"
            placeholder="Program Name"
            className="mentorship-form-input"
            value={name}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>
        <span className={"input-label"}>Role Labels</span>
        <span className={"input-label-sub"}>How should mentor and mentee roles be labeled? For example, Mentor and Mentee, or Preceptor and Resident.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[{ required: true, message: "Please input the mentor label" }]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="mentor-label"
            placeholder="Mentor Label"
            className="mentorship-form-input"
            value={mentorLabel}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMentorLabel(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>
        <Form.Item
          className="mentorship-form-item"
          rules={[{ required: true, message: "Please input the mentee label" }]}
          required
          validateTrigger="onBlur"
        >
          <Input
            name="mentee-label"
            placeholder="Mentee Label"
            className="mentorship-form-input"
            value={menteeLabel}
            autoComplete="off"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setMenteeLabel(event.target.value);
            }}
            allowClear
            disabled={isLoading}
          />
        </Form.Item>        
        <span className={"input-label"}>Matching Method</span>
        <span className={"input-label-sub"}>How should program participants be matched? If Self-Selected, participants can send, receive, and review mentorship requests from colleagues. If Admin Managed, you will match participants after this program has been created. </span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select matching method",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Select
            value={matchingMethod}
            className="mentorship-form-input small"
            onChange={(value) => setMatchingMethod(value)}
            options={matchingMethodOptions}
            placeholder="Matching Method"
            allowClear
            disabled={isLoading}
            suffixIcon={
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                alt="dropdown icon"
                className="chevron-button"
              />
            }
          />
        </Form.Item>
        <span className={"input-label"}>Availability</span>
        <span className={"input-label-sub"}>A program can be limited to a single location, or available to all eligible participants across the organization.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select the Location Availability",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <Select
            value={availability}
            className="mentorship-form-input small"
            onChange={(value) => setAvailability(value)}
            options={availabilityOptions}
            placeholder="Location Availability"
            allowClear
            disabled={workspaceLocationsLoading}
            suffixIcon={
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                alt="dropdown icon"
                className="chevron-button"
              />
            }
          />
        </Form.Item>
        <span className={"input-label"}>{'Surveys (Optional)'}</span>
        <span className={"input-label-sub"}>For each survey, specify which participants should be prompted, and provide a valid link.</span>
        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={preSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setPreSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Pre Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: preSurveyLinkRequired,
                message: "Please enter the Pre Survey URL.",
              },
            ]}
            required={preSurveyLinkRequired}
            validateStatus={
              preSurveyLinkRequired && !preSurveyLink ? "error" : ""
            }
            help={
              preSurveyLinkRequired && !preSurveyLink
                ? "Please enter the Pre Survey URL."
                : undefined
            }
          >
            <Input
              name="preSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={preSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPreSurveyLink(event.target.value);
              }}
              allowClear
              required={preSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>

        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={midSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setMidSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Mid Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: midSurveyLinkRequired,
                message: "Please enter the Mid Survey URL.",
              },
            ]}
            required={midSurveyLinkRequired}
            validateStatus={
              midSurveyLinkRequired && !midSurveyLink ? "error" : ""
            }
            help={
              midSurveyLinkRequired && !midSurveyLink
                ? "Please enter the Mid Survey URL."
                : undefined
            }
          >
            <Input
              name="midSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={midSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setMidSurveyLink(event.target.value);
              }}
              allowClear
              required={midSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>

        <div className="mentorship-input-row">
          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
          >
            <Select
              value={postSurveyAvailability}
              className="mentorship-form-input small"
              onChange={(value) => setPostSurveyAvailability(value)}
              options={surveyOptions}
              placeholder="Post Survey"
              allowClear
              disabled={workspaceLocationsLoading}
              suffixIcon={
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/black-chevron-down.svg"
                  }
                  alt="dropdown icon"
                  className="chevron-button"
                />
              }
            />
          </Form.Item>

          <Form.Item
            className="mentorship-form-paired-item"
            validateTrigger="onBlur"
            rules={[
              {
                required: postSurveyLinkRequired,
                message: "Please enter the Post Survey URL.",
              },
            ]}
            required={postSurveyLinkRequired}
            validateStatus={
              postSurveyLinkRequired && !postSurveyLink ? "error" : ""
            }
            help={
              postSurveyLinkRequired && !postSurveyLink
                ? "Please enter the Post Survey URL."
                : undefined
            }
          >
            <Input
              name="postSurveyLink"
              placeholder="If yes, enter URL here"
              className="mentorship-form-input paired"
              value={postSurveyLink}
              autoComplete="off"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPostSurveyLink(event.target.value);
              }}
              allowClear
              required={postSurveyLinkRequired}
              disabled={isLoading}
            />
          </Form.Item>
        </div>
        {/* <span className={"input-label"}>Number of Check-Ins</span>
        <span className={"input-label-sub"}>Specify the number of scheduled Check-Ins for program participants to complete. Check-In details can be managed below.</span>
        <Form.Item
          className="mentorship-form-item"
          rules={[
            {
              required: true,
              message: "Please select the Number of Check-ins",
            },
          ]}
          required
          validateTrigger="onBlur"
        >
          <InputNumber
            className="mentorship-form-input small"
            min={1}
            max={20}
            onChange={(value: number | null) => {
              if (value) {
                setModulesQuantity(value);
              }
            }}
            placeholder="Number of Check-Ins"
            value={modulesQuantity}
            disabled={isLoading}
          />
        </Form.Item> */}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={modules.map((_, idx) => idx)}
          strategy={verticalListSortingStrategy}
        >
          {modules.map((m, i) => (
            <SortableModuleItem
              key={i}
              i={i}
              m={m}
              isLoading={isLoading}
              updateModule={updateModule}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="mentorship-form-buttons">
        <button
          className="mentorship-cancel-button"
          onClick={() => {
            if (programId) {
              const path = `${routeNames.MENTORSHIP_DETAILS.replace(
                ":programId",
                `${programId}`
              )}`;

              navigate(path);
            } else {
              navigate(routeNames.MENTORSHIP);
            }
          }}
        >
          Cancel
        </button>
        <button className="mentorship-save-button" onClick={handleOnSubmit}>
          Save
        </button>
      </div>
    </Container>
  );
}
